import React, { FC, useCallback, useEffect, useState } from "react";
import { service } from "../../../services/AxiosServices";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import ShoweyeIcon from "../../static/svg/ShoweyeIcon";
import HideeyeIcon from "../../static/svg/HideeyeIcon";
import logo from "../../static/images/Logo/Tcs.svg"

interface Permission {
  [key: string]: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
  };
}

interface RolePermissions {
  [role: string]: Permission;
}

interface Account {
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  permissions: RolePermissions;
  project: number;
  application: number;
  logo?:string
}

interface LoginResponse {
  auth_type: string;
  refresh_token: string;
  access_token: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  roles: string[];
  permissions: {
    [role: string]: {
      [permission: string]: {
        can_create: boolean;
        can_read: boolean;
        can_update: boolean;
        can_delete: boolean;
      };
    };
  };
  project: number;
  application: number;
}

const LoginForm: FC = () => {
  const [formState, setFormState] = useState({
    values: {
      username: "",
      password: "",
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });

  const { user, dispatch } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  // Validation function for form fields (username and password)
  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.username.trim()) {
      errors.username = "Username is required";
    }
    if (!values.password.trim()) {
      errors.password = "Password is required";
    }
    return errors;
  };

  // Change handler for updating form values and clearing errors
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
      clientErrors: {},
      serverError: null,
    }));
  }, []);

  // Function to transform permissions into a desired format for role-based access
  const transformPermissions = (
    permissionsData: LoginResponse["permissions"]
  ): RolePermissions => {
    const transformedPermissions: RolePermissions = {};

    Object.entries(permissionsData).forEach(([roleName, rolePermissions]) => {
      transformedPermissions[roleName] = {};

      Object.entries(rolePermissions).forEach(
        ([permissionName, permissionValues]) => {
          transformedPermissions[roleName][permissionName] = {
            create: permissionValues.can_create,
            read: permissionValues.can_read,
            update: permissionValues.can_update,
            delete: permissionValues.can_delete,
          };
        }
      );
    });

    return transformedPermissions;
  };
  // Handle form submission, validate inputs, make login API call, and store tokens
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const errors = validations(formState.values);
      if (Object.keys(errors).length === 0) {
        try {
          const response = await service<LoginResponse>("/login", "post", {
            username: formState.values.username,
            password: formState.values.password,
          });

          const defaultToken = response?.data?.access_token;

          if (defaultToken) {
            localStorage.setItem("access_token", defaultToken);
            localStorage.setItem(
              "project_id",
              response.data.project.toString()
            );
            localStorage.setItem(
              "application",
              response.data.application.toString()
            );
            localStorage.setItem("user", response.data.user.id.toString());
            const responseData = response?.data;

            const userData = response?.data?.user;
            const userRoles = response?.data?.roles || ["Admin"];

            const formattedPermissions = transformPermissions(
              response.data.permissions
            );

            let loginDetails: Account = {
              id: userData.id.toString(),
              email: userData.email,
              first_name: userData.first_name,
              last_name: userData.last_name,
              roles: userRoles,
              permissions: formattedPermissions,
              project: responseData.project,
              application: responseData.application,
              // logo:logo
            };
            localStorage.setItem("user_account", JSON.stringify(loginDetails));

            dispatch({ type: "LOGIN", payload: { account: loginDetails } });
            toast.success("Logged in successfully!", { autoClose: 2000 });
            navigate("/");
          }
        } catch (err: any) {
          const errorMessage =
            err.response?.data?.detail ||
            "Something went wrong. Please try again..";
          setFormState((prev) => ({ ...prev, serverError: errorMessage }));
        }
      } else {
        setFormState((prev) => ({ ...prev, clientErrors: errors }));
      }
    },
    [formState.values, dispatch, navigate]
  );

  const handleForgotPassword = () => {
    navigate("/forget-password");
  };

  // Convert client-side validation errors to an array for display
  const clientErrorsArray = Object.entries(formState.clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );
  // Toggle password visibility on the form
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  useEffect(() => {
    console.log(user);
  }, [user]);
  return (
    <>
      {formState.serverError && (
        <div className="alert alert-danger">{formState.serverError}</div>
      )}
      {/* {clientErrorsArray.length > 0 && (
        <div className="alert alert-danger">
          <ul>
            {clientErrorsArray.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )} */}

      <form onSubmit={handleSubmit}>
        <p className="text-blue text-center">
          Sign in by credentials
        </p>
        <div className="mb-3">
          <label htmlFor="username" className="form-label LabelText">
            Username<span className="text-danger"></span>
          </label>
          <input
            type="text"
            className="form-control shadow-none border-blue rounded-0 text-blue"
            id="username"
            name={"username"}
            value={formState.values.username}
            onChange={handleChange}
          />
          {formState.clientErrors.username && (
            <small className="text-danger">
              {formState.clientErrors.username}
            </small>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="password" className="form-label LabelText">
            {" "}
            Password<span className="text-danger"></span>
          </label>
          <div className="position-relative">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control shadow-none border-blue rounded-0 text-blue"
              name="password"
              id="password"
              value={formState.values.password}
              onChange={handleChange}
            />
            <p
              className="Lgin-eyeicon mt-2 cursor-pointer"
              onClick={togglePasswordVisibility}
              style={{ cursor: "pointer" }}
            >
              {showPassword ? <ShoweyeIcon /> : <HideeyeIcon />}
            </p>
          </div>


          {formState.clientErrors.password && (
            <small className="text-danger">
              {formState.clientErrors.password}
            </small>
          )}
        </div>

        <div className="text-center mb-3">
          <button
            type="submit"
            className="LoginButton shadow-none   w-100 py-2 rounded"
          >
            LOGIN
          </button>
        </div>
        <div className="text-center">
          <button
            className="btn btn-link text-blue text-decoration-none"
            type="button"
            onClick={handleForgotPassword}
          >
            Forgot password?
          </button>
        </div>
      </form>
    </>
  );
};

export default LoginForm;
